import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { formatCurrency, useConfirmOrder } from '@/services/order-service';
import { OrderItemResponse, OrderResponse, OrderStatus } from '@/types/order-type';
import { calculateOrderTotals } from '@/utils/calc-order';
import { Box, Typography } from '@mui/material';
import { CheckCircle, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { OrderStatusBadge } from '../sale/OrderStatusBadge';
import { Badge } from '../ui/badge';
import { CashOnDeliveryBadge } from './CashOnDeliveryBadge';

interface OrderSummaryProps {
  order: OrderResponse;
  orderItems: OrderItemResponse[];
  onConfirmOrder?: () => void;
  onCancelOrder?: () => void;
}

export default function OrderSummary({
  order,
  orderItems,
  onConfirmOrder,
  onCancelOrder,
}: OrderSummaryProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { execute: confirmOrderMutation } = useConfirmOrder();

  const { subtotal, shippingFee, discount, tax, total, totalItems } = useMemo(() => {
    return calculateOrderTotals({
      items: orderItems,
      deliveryFee: Number(order.deliveryFee ?? 2),
      discountPercentage: Number(order.discountPercentage ?? 0),
      taxRate: 0,
    });
  }, [orderItems, order.deliveryFee, order.discountPercentage]);

  const isDraft = order.status === OrderStatus.DRAFT;
  const isCancel = order.status === OrderStatus.CANCELLED;
  const isPaid = order.status === OrderStatus.PAID;

  const handleConfirmOrder = async () => {
    setIsConfirming(true);
    try {
      await confirmOrderMutation(order.id);
      toast({
        title: 'Success',
        description: 'Order confirmed successfully. Stock has been updated.',
      });
      onConfirmOrder?.();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to confirm order. Please check stock availability.',
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl">
      {/* Header */}
      <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
        <Box className="flex items-center gap-1">
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              color: 'text.primary',
            }}
          >
            Order Summary
          </Typography>
          {order.cashOnDelivery && <CashOnDeliveryBadge isCashOnDelivery={order.cashOnDelivery} />}
        </Box>
        <div className="flex gap-2">
          {(!isCancel && !isPaid) && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                className="h-8 px-3 border border-red-300 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-400"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Cancel Order?</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to cancel this order? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className='gap-2'>
                    <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                      Keep Order
                    </Button>
                    <Button
                      className="bg-red-600 text-white hover:bg-red-700"
                      onClick={() => {
                        setShowCancelDialog(false);
                        onCancelOrder?.();
                      }}
                    >
                      Yes, Cancel
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
          {isDraft && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConfirmDialog(true)}
                disabled={isConfirming}
                className="h-8 px-3 border border-blue-500 text-blue-600 hover:text-blue-700 hover:bg-blue-50 hover:border-blue-600"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {isConfirming ? 'Confirming...' : 'Confirm'}
              </Button>
              <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Confirm Order?</DialogTitle>
                    <DialogDescription>
                      Confirming this order will update stock levels. Make sure all items and
                      quantities are correct before proceeding.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                      Go Back
                    </Button>
                    <Button
                      className="bg-blue-600 text-white hover:bg-blue-700"
                      onClick={() => {
                        setShowConfirmDialog(false);
                        handleConfirmOrder();
                      }}
                    >
                      Yes, Confirm
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="px-4 pb-4">
        {/* Compact totals */}
        <div className="space-y-2">
          <Row label="Subtotal" value={formatCurrency(String(subtotal))} />
          {discount > 0 && (
            <Row
              label="Discount"
              value={`-${formatCurrency(String(discount))}`}
              valueClass="text-emerald-600"
            />
          )}

          <div className="flex items-center justify-between py-1.5">
            <span className="text-xs font-medium text-slate-600">Total Items</span>
            <div className="text-right">
              <Badge className="font-bold">{totalItems}</Badge>
            </div>
          </div>
          <Row label="Delivery Fee" value={formatCurrency(String(shippingFee))} />
          <Row label="Tax (0%)" value={formatCurrency(String(tax))} />

          <Separator className="my-3" />

          <div className="flex items-center justify-between rounded-lg border-2 border-blue-200 bg-slate-50 px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">Total</span>
            <span className="text-lg font-bold text-blue-700">{formatCurrency(String(total))}</span>
          </div>
        </div>

        {/* Meta */}
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs font-medium text-slate-600">Assigned To</span>
              <span className="text-sm font-semibold text-slate-800">
                {order.followUpUser || 'NA'}
              </span>
            </div>

            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs font-medium text-slate-600">Cash On Delivery</span>
              <span className="text-sm font-semibold text-slate-800">
                {order.cashOnDelivery ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5">
            <span className="text-xs font-medium text-slate-600">Status</span>
            <div className="text-right">
              <OrderStatusBadge status={order.status} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** small helper for tidy rows */
function Row({
  label,
  value,
  valueClass = '',
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <span className={`text-sm font-semibold text-slate-800 ${valueClass}`}>{value}</span>
    </div>
  );
}
